import { Box3, Mesh, Object3D, Quaternion, Vector3 } from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import { forEachAsync } from "../util/Collection";
import { isObbDebugDrawEnabled } from "../util/DebugFlags";
import { disposeObbDebugGroup } from "../debug/dragCollisionDebugDraw";
import {
  calculateBoundingBoxAll,
  createObbWireframeHelper,
  decomposeIntoOBBs,
  disposeObject3DDeep,
} from "../util/GeometryUtil";
import { logger } from "../util/Logger";
import { LoaderManager } from "./Loader";
import { Sugar3DLoader } from "./Sugar3DLoader";
import { SugarModelJoint } from "./SugarModelJoint";
import {
  createMaterial,
  createPart,
  SugarMaterial,
  SugarModelBase,
  SugarPart,
  SugarPartGroup,
} from "./SugarModelLoader";

export interface SugarAsset {
  get data(): any;
  get id(): any;
  get name(): string;
  get code(): string;
  get thumbnailFileUrl(): string;
  get loaded(): boolean;
}
export class SugarModel extends SugarModelBase implements SugarAsset {
  parts?: SugarPart[];
  partMaterialGroups?: SugarPartGroup[];
  materials?: SugarMaterial[];
  private _joints?: SugarModelJoint[];

  //protected _root: Object3D = new Object3D();
  private _model: Object3D | undefined;
  private _data: any;
  private _bound?: Box3;
  private _bounds: Mesh[] = [];
  private _loaded = false;

  constructor(data: any) {
    super();
    this._data = data;
    this.name = data.name || data.id || "SugarModel";
    //this.add(this._root);
  }

  get data(): any {
    return this._data;
  }
  /*get id(): string {
     return this._data.id;
   }
   get name(): string {
     return this._data.name;
   }*/
  get code(): string {
    return this._data.id;
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl;
  }

  get model(): Object3D | undefined {
    return this._model;
  }

  get bound() {
    return this._bound;
  }
  get bounds() {
    return this._bounds;
  }

  get joints() {
    return this._joints;
  }

  set joints(value) {
    this._joints = value;
  }

  private _localObb = new OBB();
  private _worldObb = new OBB();
  private _localObbs: OBB[] = [];
  private _obbDebugGroup: Object3D | null = null;

  private _clearObbDebugIfAny() {
    if (this._obbDebugGroup) {
      disposeObbDebugGroup(this._obbDebugGroup);
      this._obbDebugGroup = null;
    }
  }

  /**
   * Yeni glTF gelene kadar çarpışma/obb listesi eski mesh’e bağlı kalmasın (`loaded` hâlâ true olabilir).
   */
  private _invalidateCollisionBoundsForReload(): void {
    this._clearObbDebugIfAny();
    this._localObbs = [];
    this._localObb = new OBB();
    this._bound = undefined;
  }

  private _syncObbDebugVisualization() {
    this._clearObbDebugIfAny();
    if (!this._model || !isObbDebugDrawEnabled()) return;

    const group = new Object3D();
    group.name = "obbDebugGroup";
    for (const lo of this._localObbs) {
      group.add(createObbWireframeHelper(lo, 0x00e676));
    }
    group.add(createObbWireframeHelper(this._localObb, 0xff9100));
    this._model.add(group);
    this._obbDebugGroup = group;
  }

  get localObb() {
    const localObb = this._localObb.clone();
    (localObb as any).model = this;
    return localObb;
  }

  get obb() {
    this._worldObb.copy(this._localObb);
    this._worldObb.applyMatrix4(this.matrixWorld);
    (this._worldObb as any).model = this;
    return this._worldObb;
  }

  get localObbs(): OBB[] {
    return this._localObbs.map((o) => {
      const c = o.clone();
      (c as any).model = this;
      return c;
    });
  }

  get obbs(): OBB[] {
    return this._localObbs.map((o) => {
      const c = o.clone();
      c.applyMatrix4(this.matrixWorld);
      (c as any).model = this;
      return c;
    });
  }

  set model(value: Object3D) {
    if (this._model && this._model != value) {
      this._clearObbDebugIfAny();
      this._model?.removeFromParent();
      (this._model as any).model = undefined;
    }

    this._model = value;
    if (!this.model) {
      logger.warn("Model is undefined!!!");
      return;
    }

    const parent = this._model?.parent;
    parent?.remove(this._model);

    const originPosition = this._model.position.clone();
    const originScale = this._model.scale.clone();
    const originRotation = this._model.quaternion.clone();
    this._model.position.set(0, 0, 0);
    this._model.scale.set(1, 1, 1);
    this._model.quaternion.copy(new Quaternion());

    (this._model as any).model = this;
    this._bounds = calculateBoundingBoxAll(this._model);

    const box = new Box3();
    box.setFromObject(this._model, true);
    this._model.userData.bound = box;
    this._bound = box;

    const size = new Vector3();
    this._bound.getSize(size);
    const obb = new OBB();
    box.getCenter(obb.center);
    obb.halfSize.copy(size.multiplyScalar(0.5));
    this._localObb = obb;

    this._localObbs = decomposeIntoOBBs(this._model, box);

    this.root.add(this._model);
    this._model.updateMatrixWorld(true);

    parent?.add(this._model);
    this._model.position.copy(originPosition);
    this._model.scale.copy(originScale);
    this._model.quaternion.copy(originRotation);

    this._syncObbDebugVisualization();
  }

  get loaded() {
    return this._loaded;
  }

  set loaded(value: boolean) {
    this._loaded = value;
  }

  async _loadModel(
    sugarModel: SugarModel,
    loadPromises: Promise<any[]>,
    onAfterLoad?: (sugarModel: SugarModel, results: any[]) => void
  ) {
    const results = await loadPromises;

    logger.debug("all load finihed");
    sugarModel.model = results[0];

    sugarModel.parts?.forEach((part) => {
      part.apply(sugarModel);
    });

    sugarModel.partMaterialGroups?.forEach((part) => {
      part.apply(sugarModel);
    });

    sugarModel._joints?.forEach((joint) => {
      joint.apply(this);
    });

    sugarModel.loaded = true;

    if (onAfterLoad) onAfterLoad(sugarModel, results);
    return sugarModel;
  }

  async load(
    data: any,
    options?: {
      forceSync?: boolean;
      forceAgain?: boolean;
      onBeforeLoad?: (product: SugarModel) => void;
      onAfterLoad?: (sugarModel: SugarModel, results: any[]) => void;
      onProgress?: (progress: ProgressEvent) => void;
    }
  ): Promise<SugarModel | undefined> {
    if (this.loaded && !options?.forceAgain) return this;

    this.model?.removeFromParent();
    this._invalidateCollisionBoundsForReload();
    this.loaded = false;

    this.name = data.name || data.id || "SugarModel";

    this.userData["info"] = data;
    if (data.jointInfo && data.jointInfo.modelJoints) {
      this._joints = data.jointInfo.modelJoints?.map((joint: any) => {
        return new SugarModelJoint(joint);
      });
    } else if (Array.isArray(data.joints) && data.joints.length > 0) {
      this._joints = data.joints.map((joint: any) => new SugarModelJoint(joint));
      this.userData["info"].jointInfo = {
        id: data.id,
        excludes: data.excludes ?? [],
        modelJoints: data.joints,
      };
    }

    let materials: SugarMaterial[] = [];
    if (data.parts) {
      materials = data.materials?.map((material: any) => {
        return createMaterial(material);
      });
    }
    //
    let parts: SugarPart[] = [];
    if (data.parts) {
      parts = data.parts.map((part: any) => {
        return createPart(part);
      });
    }

    let partMaterialGroups;
    if (data.partMaterialGroups) {
      partMaterialGroups = data.partMaterialGroups.map(
        (partMaterialGroup: any) => {
          return new SugarPartGroup(partMaterialGroup, materials, parts);
        }
      );
    }

    //const sugarModel = new SugarModel(data);
    const sugarModel = this;
    sugarModel._data = data;
    sugarModel.materials = materials;
    sugarModel.parts = parts;
    sugarModel.partMaterialGroups = partMaterialGroups;
    //modelCache.set(data.id, sugarModel);
    //load begins

    const asyncFunctions = [];
    const loader = new LoaderManager(new Sugar3DLoader());
    asyncFunctions.push(
      loader.load(data.gltf, { onProgress: options?.onProgress }, true)
    );

    if (parts) {
      const partLoaders = forEachAsync(parts, async (part: any) => {
        await part.load();
      });
      asyncFunctions.push(partLoaders);
    }

    //
    if (partMaterialGroups) {
      const partLoaders = forEachAsync(
        partMaterialGroups,
        async (partMaterialGroupLoader: any) => {
          await partMaterialGroupLoader.load();
        }
      );
      asyncFunctions.push(partLoaders);
    }

    if (options?.onBeforeLoad) options?.onBeforeLoad(sugarModel);

    if (options?.forceSync) {
      await this._loadModel(
        sugarModel,
        Promise.all(asyncFunctions),
        options?.onAfterLoad
      );

      return sugarModel;
    } else {
      logger.debug("before this.loadModel", asyncFunctions);
      this._loadModel(
        sugarModel,
        Promise.all(asyncFunctions),
        options?.onAfterLoad
      );
      return sugarModel;
    }
  }

  dispose(): void {
    this._clearObbDebugIfAny();
    if (this._model) {
      disposeObject3DDeep(this._model);
      this._model.removeFromParent();
      this._model = undefined;
    }
    for (const b of this._bounds) {
      disposeObject3DDeep(b);
      b.removeFromParent();
    }
    this._bounds = [];
    this._bound = undefined;
    this._localObbs = [];
  }
}
